// THE DEVICE AND THE IO ARE TWO QUESTIONS — the decision, with no I/O.
//
// It lives apart from `gpuDevice.ts` for two reasons. It is the rule that was
// wrong, so it is the rule worth locking; and `gpuDevice.ts` imports
// extensionlessly, which only a bundler resolves, so a test that drove it
// directly could not run under `npm run test:unit` (the same constraint that
// put `materialBinding.test.js` under vitest). Restating the rule inside a test
// would have let the two drift — which is precisely the failure mode being
// fixed here — so the rule moved instead.
//
// WHAT WAS WRONG. `OgsLiftMode` read `"shared" | "own" | "cpu"`, from the era
// when ORT-Web's `env.webgpu.adapter` was READ-ONLY: the page could not give ORT
// a device, only nudge it toward an adapter and read back whatever it built, so
// two devices on one page was a real outcome and `"own"` named it. ORT-Web 1.26
// lets the device be SET, `sharedGpuDevice.js` makes one at boot for three AND
// ORT, and `getOgsComputeDevice` asks for that one first — so `"own"` handed
// back the very same object under a different name.
//
// The damage was downstream, where `sharedDevice = mode === "shared" ? device : null`
// is exactly `gpuBufferIo ? device : null`. A consumer that wanted only "is
// there a device" was answered "no" whenever the decoder IO was off, so
// `?gpubuf=0` — which names the latent handoff and nothing else — ALSO turned
// off the GPU plücker, refused the on-device splat overlay, and left every live
// branch riding its rigid anchor. One flag, four effects, three unnamed.

/**
 * Which device the lift should run on, and whether the decoder outputs have to
 * come back through the CPU. These are independent: only `gpuLift` (the flag
 * that names it) and the absence of WebGPU can take the lift off the GPU.
 *
 * @param {object} o
 * @param {boolean} o.ioRequested      decoder outputs were asked to stay on the device
 *                                     (`prepareOgsLiftDevice`'s `shared`: ?gpubuf & ?gpulift & ?gpubuflift)
 * @param {boolean} o.ortDeviceUsable  ORT's device exists and clears the lift's storage-buffer floor
 * @param {boolean} o.gpuLift          ?gpulift — the one switch that means "lift on the CPU"
 * @returns {{ source: 'ort'|'page'|'none', demoted: boolean }}
 *   `source` is WHERE to get the device; `demoted` is about the OUTPUTS alone.
 */
export function planLiftDevice({ ioRequested, ortDeviceUsable, gpuLift }) {
  // The ordinary page: ORT holds the device the page made, and the outputs stay on it.
  if (ioRequested && ortDeviceUsable) return { source: 'ort', demoted: false }

  // The outputs were asked to stay on the device and ORT's device cannot serve
  // them. The gpu-buffer hints are baked into the sessions at CREATE time, so the
  // only repair is to download every output — but that is a statement about the
  // IO. The lift is unaffected and stays on the page's device.
  if (ioRequested) return { source: gpuLift ? 'page' : 'none', demoted: true }

  // Nobody asked for on-device IO (`?gpubuf=0`, a non-WebGPU EP). That is a
  // CHOICE about the outputs, not a demotion, and it leaves the lift alone.
  return { source: gpuLift ? 'page' : 'none', demoted: false }
}

/** There is one device or there is none — never a second one under another name. */
export function liftModeFor(device) {
  return device ? 'gpu' : 'cpu'
}
